import { setAskDraft, setAskStep, submitAsk } from "@worktools/shared";
import { useBoard } from "../sync/BoardContext";
import { useMyOpenAsk } from "../sync/hooks";
import { useDialogs } from "../ui/dialogs";
import { IconCheck } from "./icons";

/**
 * Claude's questions. In a board wide ask everybody works on the same selection,
 * so the group sends one answer instead of contradicting each other.
 */
export function AskPanel() {
  const { doc, me } = useBoard();
  const dialogs = useDialogs();
  const ask = useMyOpenAsk();

  if (!ask) return null;

  const me_ = { userId: me.id, userName: me.name };
  const shared = ask.audience.kind === "board";
  const step = Math.min(ask.draft.step, ask.questions.length - 1);
  const question = ask.questions[step];
  const answer = ask.draft.answers[step] ?? { selected: [], other: "" };
  const isLast = step === ask.questions.length - 1;
  const hasAnswer = answer.selected.length > 0 || answer.other.trim().length > 0;
  const lastEditor =
    shared && ask.draft.updatedByName && ask.draft.updatedBy !== me.id
      ? ask.draft.updatedByName
      : null;

  const toggle = (label: string) => {
    const selected = question.multiSelect
      ? answer.selected.includes(label)
        ? answer.selected.filter((item) => item !== label)
        : [...answer.selected, label]
      : answer.selected.includes(label)
        ? []
        : [label];
    setAskDraft(doc, ask.id, step, { selected }, me_);
  };

  const next = () => {
    if (!isLast) {
      setAskStep(doc, ask.id, step + 1, me_);
      return;
    }
    submitAsk(doc, ask.id, me_);
    dialogs.toast("Antwort an Claude gesendet", "ai");
  };

  return (
    <div className="review review--ask">
      <header className="review__head">
        <img src="/claude-logo.svg" alt="" />
        <div>
          <span className="label">
            {question.header || `Claude fragt${shared ? " alle" : ""}`}
          </span>
          <h3>{question.question}</h3>
        </div>
        {ask.questions.length > 1 && (
          <span className="review__queue">
            {step + 1}/{ask.questions.length}
          </span>
        )}
      </header>

      <div className="ask__options">
        {question.options.map((option) => {
          const active = answer.selected.includes(option.label);
          return (
            <button
              key={option.label}
              className={`ask__option${active ? " ask__option--active" : ""}`}
              onClick={() => toggle(option.label)}
            >
              <span className={`ask__mark${question.multiSelect ? " ask__mark--box" : ""}`}>
                {active && <IconCheck size={11} />}
              </span>
              <span className="ask__label">
                {option.label}
                {option.description && <small>{option.description}</small>}
              </span>
            </button>
          );
        })}
      </div>

      {question.allowOther !== false && (
        <input
          type="text"
          className="ask__other"
          placeholder="Sonstiges…"
          value={answer.other}
          onChange={(event) => setAskDraft(doc, ask.id, step, { other: event.target.value }, me_)}
          onKeyDown={(event) => event.key === "Enter" && hasAnswer && next()}
        />
      )}

      {shared && (
        <p className="ask__shared">
          {lastEditor ? `${lastEditor} hat gerade geändert` : "Ihr entscheidet gemeinsam"}
        </p>
      )}

      <div className="review__actions">
        {step > 0 && (
          <button className="pill" onClick={() => setAskStep(doc, ask.id, step - 1, me_)}>
            Zurück
          </button>
        )}
        <button className="pill pill--solid" onClick={next} disabled={!hasAnswer}>
          {isLast ? "Antwort senden" : "Weiter"}
        </button>
      </div>
    </div>
  );
}
